import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSelector } from '@xstate/store/react';
import { Search, Trash2, X, ListTodo } from 'lucide-react';
import { globalTasksStore } from '../stores/globalTasksStore';
import { boardsStore } from '../stores/boardsStore';
import { employeesStore } from '../employees/employeesStore';
import { initBoardTaskViewStore } from '../boardv3/stores/boardTaskViewStore';
import { initBoardColumnsStore } from '../boardv3/stores/columnsStore';
import { recalculateSchedule } from '../boardv3/stores/roadmapStore';
import { fuzzyFilter, highlightMatches } from '../boardv3/utils/fuzzySearch';
import { MultiSelectDropdown, type DropdownOption } from '../components/MultiSelectDropdown';
import { HighlightedText } from '../components/HighlightedText';
import { TaskModal } from '../components/tasks';
import { useTaskModalState } from '../hooks/useTaskModalState';
import type { GlobalTask, Employee, BoardDefinition, PertEstimate, Task } from '../types';
import { PageContainer, PageHeader, PageEmptyState } from '../components/PageLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// ============================================
// Types
// ============================================

interface BoardPresence {
  boardId: string;
  boardName: string;
  column: string;
}

interface TaskWithBoardInfo {
  task: GlobalTask;
  boardPresence: BoardPresence[];
}

// ============================================
// Data Hook
// ============================================

function useGlobalTasksWithBoardInfo(): {
  enrichedTasks: TaskWithBoardInfo[];
  employees: Employee[];
  boards: BoardDefinition[];
  allColumns: string[];
} {
  const globalContext = useSelector(globalTasksStore, (s) => s.context);
  const boards = useSelector(boardsStore, (s) => s.context.boards);
  const employees = useSelector(employeesStore, (s) => s.context.employees);

  // Force re-render when board view stores change
  const [, forceUpdate] = useState(0);
  const boardViewUnsubs = useRef<{ unsubscribe: () => void }[]>([]);

  useEffect(() => {
    for (const sub of boardViewUnsubs.current) sub.unsubscribe();
    boardViewUnsubs.current = [];

    for (const board of boards) {
      const store = initBoardTaskViewStore(board.id);
      const sub = store.subscribe(() => {
        forceUpdate((n) => n + 1);
      });
      boardViewUnsubs.current.push(sub);
    }

    return () => {
      for (const sub of boardViewUnsubs.current) sub.unsubscribe();
      boardViewUnsubs.current = [];
    };
  }, [boards]);

  const enrichedTasks = useMemo(() => {
    const tasks = globalContext.taskIds.map((id) => globalContext.tasksById[id]).filter(Boolean);
    const columnsSet = new Set<string>();

    const result: TaskWithBoardInfo[] = tasks.map((task) => {
      const boardPresence: BoardPresence[] = [];
      for (const board of boards) {
        const store = initBoardTaskViewStore(board.id);
        const views = store.getSnapshot().context.views;
        const view = views[task.id];
        if (view) {
          boardPresence.push({
            boardId: board.id,
            boardName: board.name,
            column: view.column,
          });
          columnsSet.add(view.column);
        }
      }
      return { task, boardPresence };
    });

    return result;
  }, [globalContext, boards]);

  const allColumns = useMemo(() => {
    const set = new Set<string>();
    for (const { boardPresence } of enrichedTasks) {
      for (const bp of boardPresence) {
        set.add(bp.column);
      }
    }
    return Array.from(set).sort();
  }, [enrichedTasks]);

  return { enrichedTasks, employees, boards, allColumns };
}


// ============================================
// Status Toggle
// ============================================

type StatusFilter = 'all' | 'active' | 'unassigned';

function StatusToggle({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const { t } = useTranslation();
  const options: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('taskStatus.all') },
    { value: 'active', label: t('taskStatus.onBoard') },
    { value: 'unassigned', label: t('taskStatus.noBoard') },
  ];

  return (
    <div className="flex rounded border border-gray-200 overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-2.5 py-1 text-xs transition-colors ${
            value === opt.value
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}


// ============================================
// Estimate Display
// ============================================

function formatEstimate(task: GlobalTask, t: TFunction): string {
  if (task.estimateMode === 'default' && task.defaultEstimate) {
    const weeks = pertExpectedWeeks(task.defaultEstimate);
    return `${weeks.toFixed(1)}W`;
  }
  const count = Object.keys(task.estimates).length;
  if (count === 0) return '-';
  return t('taskTable.individualCount', { count });
}

function pertExpectedWeeks(est: PertEstimate): number {
  return (est.o + 4 * est.m + est.p) / 6 / 5;
}

// ============================================
// TaskRow
// ============================================

function TaskRow({
  item,
  employeeMap,
  searchQuery,
  onDelete,
  onSelect,
}: {
  item: TaskWithBoardInfo;
  employeeMap: Map<string, Employee>;
  searchQuery: string;
  onDelete: (taskId: string) => void;
  onSelect: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  const { task, boardPresence } = item;
  const titleSegments = highlightMatches(searchQuery, task.title);

  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onSelect(task.id)}
    >
      {/* Title */}
      <td className="px-3 py-1.5 text-xs text-gray-900 max-w-[280px]">
        <div className="truncate font-medium">
          <HighlightedText segments={titleSegments} />
        </div>
        {Array.isArray(task.description) && task.description.length > 0 && (
          <div className="text-xs text-gray-400 truncate mt-0.5">
            {t('task.description')}
          </div>
        )}
      </td>

      {/* Assignees */}
      <td className="px-3 py-1.5">
        <div className="flex flex-wrap gap-1">
          {task.assigneeIds.length === 0 && (
            <span className="text-xs text-gray-300">-</span>
          )}
          {task.assigneeIds.map((id) => {
            const emp = employeeMap.get(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 text-xs text-gray-600"
                title={emp?.name ?? id}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: emp?.color ?? '#999' }}
                />
                <span className="truncate max-w-[80px]">{emp?.name ?? id}</span>
              </span>
            );
          })}
        </div>
      </td>

      {/* Boards */}
      <td className="px-3 py-1.5">
        <div className="flex flex-wrap gap-1">
          {boardPresence.length === 0 && (
            <span className="text-xs text-gray-300 italic">{t('taskTable.noBoard')}</span>
          )}
          {boardPresence.map((bp) => (
            <span
              key={bp.boardId}
              className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
              title={`${bp.boardName}: ${bp.column}`}
            >
              {bp.boardName}: {bp.column}
            </span>
          ))}
        </div>
      </td>

      {/* Start Date */}
      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
        {task.startDate}
      </td>

      {/* Estimate */}
      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
        {formatEstimate(task, t)}
      </td>

      {/* Actions */}
      <td className="px-3 py-1.5 text-right">
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
          className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title={t('task.deleteTaskButton')}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

// ============================================
// GlobalTasksPage
// ============================================

export function GlobalTasksPage() {
  useDocumentTitle('Tasks – qtplan');
  const { t } = useTranslation();
  const { enrichedTasks, employees, boards, allColumns } = useGlobalTasksWithBoardInfo();

  const modal = useTaskModalState();

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEmployeeIds, setFilterEmployeeIds] = useState<string[]>([]);
  const [filterBoardIds, setFilterBoardIds] = useState<string[]>([]);
  const [filterColumns, setFilterColumns] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');

  // Employee lookup
  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const e of employees) map.set(e.id, e);
    return map;
  }, [employees]);

  // Dropdown options
  const employeeOptions: DropdownOption[] = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.name, color: e.color })),
    [employees]
  );

  const boardOptions: DropdownOption[] = useMemo(
    () => boards.map((b) => ({ value: b.id, label: b.name })),
    [boards]
  );

  const columnOptions: DropdownOption[] = useMemo(
    () => allColumns.map((c) => ({ value: c, label: c })),
    [allColumns]
  );

  // Apply filters
  const filteredTasks = useMemo(() => {
    let result = enrichedTasks;

    // Status filter
    if (filterStatus === 'active') {
      result = result.filter(
        ({ boardPresence }) => boardPresence.length > 0
      );
    } else if (filterStatus === 'unassigned') {
      result = result.filter(({ boardPresence }) => boardPresence.length === 0);
    }

    // Employee filter
    if (filterEmployeeIds.length > 0) {
      result = result.filter(({ task }) =>
        task.assigneeIds.some((id) => filterEmployeeIds.includes(id))
      );
    }

    // Board filter
    if (filterBoardIds.length > 0) {
      result = result.filter(({ boardPresence }) =>
        boardPresence.some((bp) => filterBoardIds.includes(bp.boardId))
      );
    }

    // Column filter
    if (filterColumns.length > 0) {
      result = result.filter(({ boardPresence }) =>
        boardPresence.some((bp) => filterColumns.includes(bp.column))
      );
    }

    // Fuzzy search (applied last for scoring/sorting)
    if (searchQuery.trim()) {
      result = fuzzyFilter(result, searchQuery, (item) => item.task.title);
    }

    return result;
  }, [enrichedTasks, searchQuery, filterEmployeeIds, filterBoardIds, filterColumns, filterStatus]);

  // Delete handler
  const handleDelete = useCallback((taskId: string) => {
    if (!confirm(t('task.deleteTaskConfirm'))) return;
    globalTasksStore.send({ type: 'task.delete', taskId });
  }, []);

  // Check if any filter is active
  const hasActiveFilters =
    filterEmployeeIds.length > 0 ||
    filterBoardIds.length > 0 ||
    filterColumns.length > 0 ||
    filterStatus !== 'all';

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setFilterEmployeeIds([]);
    setFilterBoardIds([]);
    setFilterColumns([]);
    setFilterStatus('all');
  }, []);

  // Open task in edit modal
  const handleSelectTask = useCallback((taskId: string) => {
    modal.openEdit(taskId, enrichedTasks.map(t => t.task.id));
  }, [enrichedTasks, modal.openEdit]);

  // Columns for AddTaskModal (from active board, or first board)
  const activeBoardId = useSelector(boardsStore, (s) => s.context.activeBoardId);
  const addTaskBoardId = activeBoardId ?? boards[0]?.id;
  const addTaskColumns = useMemo(() => {
    if (!addTaskBoardId) return [];
    return initBoardColumnsStore(addTaskBoardId).getSnapshot().context.columns.map(c => c.name);
  }, [addTaskBoardId]);

  // Add task handler: adds to global store + active board view
  const handleAddTask = useCallback((task: Task) => {
    const { column, ...globalFields } = task;
    const globalTask: GlobalTask = globalFields;
    globalTasksStore.send({ type: 'task.add', task: globalTask });
    if (addTaskBoardId) {
      initBoardTaskViewStore(addTaskBoardId).send({ type: 'board.addTask', taskId: task.id, column });
      recalculateSchedule(addTaskBoardId);
    }
  }, [addTaskBoardId]);

  return (
    <PageContainer wide>
      {/* Header */}
      <PageHeader
        title={t('nav.tasks')}
        subtitle={filteredTasks.length !== enrichedTasks.length ? t('task.totalTasksFiltered', { count: enrichedTasks.length, filtered: filteredTasks.length }) : t('task.totalTasks', { count: enrichedTasks.length })}
        buttonLabel={t('task.addTask')}
        onButtonClick={modal.openCreate}
      />

      {enrichedTasks.length === 0 && !hasActiveFilters ? (
        <PageEmptyState
          icon={<ListTodo size={20} />}
          title={t('task.noTasks')}
          description={t('task.noTasksDesc')}
        />
      ) : (
      <>
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('task.searchPlaceholder')}
            className="w-full pl-8 pr-7 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <MultiSelectDropdown
          options={employeeOptions}
          selected={filterEmployeeIds}
          onChange={setFilterEmployeeIds}
          placeholder={t('employees.title')}
        />
        <MultiSelectDropdown
          options={boardOptions}
          selected={filterBoardIds}
          onChange={setFilterBoardIds}
          placeholder={t('taskTable.boards')}
        />
        <MultiSelectDropdown
          options={columnOptions}
          selected={filterColumns}
          onChange={setFilterColumns}
          placeholder={t('taskTable.columns')}
        />
        <StatusToggle value={filterStatus} onChange={setFilterStatus} />
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {t('common.resetFilters')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                  {t('taskTable.title')}
                </th>
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                  {t('taskTable.assignees')}
                </th>
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                  {t('taskTable.boards')}
                </th>
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                  {t('taskTable.start')}
                </th>
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                  {t('taskTable.estimate')}
                </th>
                <th className="px-3 py-1.5 w-10" />
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((item) => (
                <TaskRow
                  key={item.task.id}
                  item={item}
                  employeeMap={employeeMap}
                  searchQuery={searchQuery}
                  onDelete={handleDelete}
                  onSelect={handleSelectTask}
                />
              ))}
              {filteredTasks.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-400">
                    {enrichedTasks.length === 0
                      ? t('task.noTasksExist')
                      : t('task.noTasksFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      </>
      )}

      {/* Task Modal (unified create + edit) */}
      <TaskModal
        isOpen={modal.isOpen}
        onClose={modal.close}
        mode={modal.mode}
        taskId={modal.taskId}
        taskHistory={modal.taskHistory}
        historyIndex={modal.historyIndex}
        onNavigate={modal.navigate}
        onAdd={handleAddTask}
        onUpdate={(task) => {
          globalTasksStore.send({ type: 'task.updateTitle', taskId: task.id, title: task.title });
          globalTasksStore.send({ type: 'task.updateStartDate', taskId: task.id, startDate: task.startDate });
          globalTasksStore.send({ type: 'task.updateCategories', taskId: task.id, categoryIds: task.categoryIds ?? [] });
          globalTasksStore.send({ type: 'task.updateDescription', taskId: task.id, description: Array.isArray(task.description) ? task.description : undefined });
          globalTasksStore.send({ type: 'task.updateConsequence', taskId: task.id, consequence: Array.isArray(task.consequence) ? task.consequence : undefined });
          globalTasksStore.send({ type: 'task.updateRequiredSkills', taskId: task.id, requiredSkills: typeof task.requiredSkills === 'string' ? task.requiredSkills : undefined });
          globalTasksStore.send({ type: 'task.updateEstimateMode', taskId: task.id, estimateMode: task.estimateMode });
          globalTasksStore.send({ type: 'task.updateEstimates', taskId: task.id, defaultEstimate: task.defaultEstimate, estimates: task.estimates });
        }}
        getTaskById={(tId) => {
          const item = enrichedTasks.find(t => t.task.id === tId);
          if (!item) return undefined;
          const firstPresence = item.boardPresence[0];
          return { ...item.task, column: firstPresence?.column ?? '-' } as Task;
        }}
        columns={addTaskColumns}
        onDelete={(taskId) => {
          globalTasksStore.send({ type: 'task.delete', taskId });
        }}
      />
    </PageContainer>
  );
}
